import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Row, Col } from 'react-bootstrap';
import Modal from 'v3/components/Modal';
import Text from 'v3/components/Text';
import Select from 'v3/components/Select';
import Input from 'v3/components/Input';
import Button from 'v3/components/Button';
import DatePicker from 'v3/components/DatePicker';
import api from 'services/api';
import {
  fetchBranchOffices,
  fetchUserCostCentersByCompany,
  fetchSelectOptions,
  fetchUsers,
} from 'utils/fetches';
import moment from 'moment';

const norm = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const getId    = (o) => (o?.id ?? o?.value ?? o?.externalId ?? o?.costCenterId ?? null);
const getLabel = (o) => (o?.label ?? o?.name ?? o?.description ?? o?.costCenterName ?? '');

const toISO = (str) => {
  const date = moment(str, 'DD/MM/YYYY', true)
  if (!date.isValid()) return '';
  return date.toISOString();
};

const asOption = (v, l) => ({
  value: v != null ? String(v) : '',
  label: (l ?? '').toString() || '—',
});

async function fetchNegotiators(search = '') {
  try {
    const { data } = await api.get('user/name', {
      params: { negotiator: search, isControlTower: true },
    });
    const arr = Array.isArray(data) ? data : [];
    return arr.map((u) => ({
      id: u?.id,
      username: u?.username || u?.name || `Usuário ${u?.id}`,
      value: u?.id,
      label: u?.username || u?.name || `Usuário ${u?.id}`,
      raw: u,
    })).filter((o) => o.id != null);
  } catch {
    return [];
  }
}

function formatExternalId(value) {
  const digits = String(value).replace(/\D/g, '');

  const year = digits.slice(0, 4); 
  const body = digits.slice(4, 10);
  const tail = digits.slice(10);

  if (digits.length <= 4) return year;
  if (digits.length <= 10) return `${year}/${body}`;
  return `${year}/${body}-${tail}`;
};

export default function AdvancedFilterModal({
  filter,
  statuses,
  show,
  onClose,
  onApply,
}) {
  const [form, setForm] = useState({
    ...filter,
    startDate: filter.startDate ? moment(filter.startDate).format('DD/MM/YYYY') : '',
    endDate: filter.endDate ? moment(filter.endDate).format('DD/MM/YYYY') : '',
  });
  const [branchesOpt, setBranchesOpt] = useState([]);
  const [customersOpt, setCustomersOpt] = useState([]);
  const [pcpOpt, setPcpOpt] = useState([]);
  const [negotiatorOpt, setNegotiatorOpt] = useState([]);
  const [costCentersOpt, setCostCentersOpt] = useState([]);
  const [recuseCostCenters, setRecuseCostCenters] = useState([]);
  const [errors, setErrors] = useState(null);

  useEffect(() => {
    const a = toISO(form.startDate);
    const b = toISO(form.endDate);
    if (form.startDate && form.endDate && a && b && a > b) {
      setErrors({
        startDate: 'A data de início precisa ser menor que a data final.',
        endDate: 'A data de fim deve ser maior que a data de início.',
      });
    } else {
      setErrors(null);
    }
  }, [form.startDate, form.endDate]);

  const activeCount = useMemo(() => {
    const isFilled = (v) =>
      (Array.isArray(v) && v.length > 0) ||
      (v && typeof v === 'object' && Object.keys(v).length > 0) ||
      (typeof v === 'string' && v.trim() !== '') ||
      (typeof v === 'number');

    const bag = [
      form.branch,
      form.customers,
      form.pcps,
      form.negotiators,
      form.costCenters,
      form.recuseCostCenters,
      form.statuses,
      form.startDate,
      form.endDate,
      form.travelNumber,
      form.travelId,     
    ];
    return bag.filter(isFilled).length;
  }, [form]);

  const searchBranches = useCallback(async (q = '') => {
    const res = await fetchBranchOffices(q);
    const options = (res || []).map((i) => asOption(getId(i) ?? i?.id, getLabel(i) ?? i?.name));
    if (q === '') setBranchesOpt(options);
    return options;
  }, []);

  const searchCustomers = useCallback(async (q = '') => {
    const res = await fetchSelectOptions('persons/customers', { search: q });
    const options = (res || []).map((i) => asOption(getId(i) ?? i?.id, getLabel(i) ?? i?.label ?? i?.name));
    if (q === '') setCustomersOpt(options);
    return options;
  }, []);

  const searchPcp = useCallback(async (q = '') => {
    const res = await fetchUsers(q);
    const options = (res || [])
      .map((i) => ({
        id: i?.id,
        value: String(i?.id),
        username: i?.username || i?.name || `Usuário ${i?.id}`,
        label: i?.username || i?.name || `Usuário ${i?.id}`,
        raw: i,
      }))
      .filter((o) => o.id != null);
    if (q === '') setPcpOpt(options);
    return options;
  }, []);

  const lastQueryRef = useRef('');
  const searchNegotiatorsCb = useCallback(async (q = '') => {
    lastQueryRef.current = q;
    const resp = await fetchNegotiators(q);
    if (lastQueryRef.current !== q) return [];
    const options = resp
      .map((o) => ({
        id: o?.id,
        username: o?.username || o?.label,
        ...asOption(o?.id, o?.label || o?.username),
        raw: o?.raw,
      }))
      .filter((o) => o.id != null);
    if (q === '') setNegotiatorOpt(options);
    return options;
  }, []);

  const searchStatuses = useCallback(async (q = '') => {
    const term = norm(q);
    return (statuses || []).filter((o) => norm(o.label).includes(term));
  }, [statuses]);

  const searchCostCenters = useCallback(async (q = '') => {
    const res = await fetchUserCostCentersByCompany(q);
    const arr = Array.isArray(res?.data) ? res.data : res;
    const options = (arr || []).map((i) => asOption(getId(i) ?? i?.id, getLabel(i) ?? i?.name));
    if (q === '') setCostCentersOpt(options);
    if (q === '') setRecuseCostCenters(options);
    return options;
  }, []);

  function onSubmit() {
    onApply({
      ...form,
      startDate: toISO(form.startDate) || null,
      endDate: toISO(form.endDate) || null,
    })
    onClose();
  }

  return (
    <Modal
      size="lg"
      show={show}
      backdrop="static"
      heading="Filtro Avançado"
      animation
      onHide={onClose}
      style={{ height: '100%' }}
      body={
        <Row className="p-1 form">
          <Col md={6} xs={12}>
            <Select.Async
              label="Filial"
              multiple
              onSearch={searchBranches}
              value={form.branch}
              onChange={(opt) => {
                setForm({
                  ...form,
                  branch: opt || null,
                })
              }}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              placeholder="Selecione..."
              id="select-adv-filial"
              options={branchesOpt}
              defaultOptions={branchesOpt}
            />
          </Col>

          <Col md={6} xs={12}>
            <Select.Async
              label="Tomador"
              multiple
              onSearch={searchCustomers}
              value={form.customers}
              onChange={(list) => {
                setForm({
                  ...form,
                  customers: Array.isArray(list) ? list : [],
                });
              }}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              placeholder="Selecione..."
              id="select-adv-tomador"
              options={customersOpt}
              defaultOptions={customersOpt}
            />
          </Col>

          <Col md={6} xs={12}>
            <Select.Async
              label="PCP"
              multiple
              onSearch={searchPcp}
              value={form.pcps}
              onChange={(list) => {
                setForm({
                  ...form,
                  pcps: Array.isArray(list) ? list : [],
                });
              }}
              getOptionLabel={(o) => o.username || o.label}
              getOptionValue={(o) => o.id ?? o.value}
              placeholder="Selecione..."
              id="select-adv-pcp"
              options={pcpOpt}
              defaultOptions={pcpOpt}
            />
          </Col>

          <Col md={6} xs={12}>
            <Select.Async
              label="Negociador"
              multiple
              onSearch={searchNegotiatorsCb}
              value={form.negotiators}
              onChange={(list) => {
                setForm({
                  ...form,
                  negotiators: Array.isArray(list) ? list : [],
                });
              }}
              getOptionLabel={(o) => o.username || o.label}
              getOptionValue={(o) => o.id ?? o.value}
              placeholder="Selecione..."
              id="select-adv-negociador"
              options={negotiatorOpt}
              defaultOptions={negotiatorOpt}
            />
          </Col>

          <Col md={6} xs={12}>
            <Select.Async
              label="Centro de custo"
              multiple
              onSearch={searchCostCenters}
              value={form.costCenters}
              onChange={(list) => {
                setForm({
                  ...form,
                  costCenters: Array.isArray(list) ? list : [],
                });
              }}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              placeholder="Selecione..."
              id="select-adv-cost-centers"
              options={costCentersOpt}
              defaultOptions={costCentersOpt}
            />
          </Col>

          <Col md={6} xs={12}>
            <Select.Async
              label="Recusa Centro de custo"
              multiple
              onSearch={searchCostCenters}
              value={form.recuseCostCenters}
              onChange={(list) => {
                setForm({
                  ...form,
                  recuseCostCenters: Array.isArray(list) ? list : [],
                });
              }}
              getOptionLabel={(o) => o.label}
              getOptionValue={(o) => o.value}
              placeholder="Selecione..."
              id="select-adv-recuse-cost-centers"
              options={recuseCostCenters}
              defaultOptions={recuseCostCenters}
            />
          </Col>

          <Col md={6} xs={12}>
            <Input
              label="ID externo"
              type="text"
              value={form.travelNumber}
              onChange={(e) => {
                setForm({
                  ...form,
                  travelNumber: formatExternalId(e?.target?.value),
                })
              }}
              placeholder="Ex: 0000/000000-00"
              id="input-adv-travel-number"
              inputMode="numeric"
              pattern="\d*"
            />
          </Col>

          <Col md={6} xs={12}>
            <Input
              label="ID da viagem"
              type="text"
              value={form.travelId}
              onChange={(e) => {
                const v = e.target?.value?.replace(/\D/g, '');
                setForm({
                  ...form,
                  travelId: v,
                });
              }}
              placeholder="Ex: 123456"
              id="input-adv-travel-id"
              inputMode="numeric"
              pattern="\d*"
            />
          </Col>

          <Col md={12} xs={12}>
            <Select.Async
              label="Status da viagem"
              multiple
              onSearch={searchStatuses}
              value={form.statuses}
              onChange={(list) => {
                setForm({
                  ...form,
                  statuses: Array.isArray(list) ? list : [],
                });
              }}
              getOptionLabel={(o) => o.name}
              getOptionValue={(o) => o.code}
              placeholder="Selecione..."
              id="select-adv-travel-status"
              options={statuses}
              defaultOptions={statuses}
            />
          </Col>

          <Col md={6} xs={12}>
            <DatePicker
              value={form.startDate}
              onChange={(e) => {
                setForm({
                  ...form,
                  startDate: e.target.value,
                });
              }}
              label="Período de"
              error={errors?.startDate}
              id="input-adv-periodo-inicio"
              format="DD/MM/YYYY"
              guide
            />
          </Col>

          <Col md={6} xs={12}>
            <DatePicker
              value={form.endDate}
              onChange={(e) => {
                setForm({
                  ...form,
                  endDate: e.target.value,
                });
              }}
              label="Até"
              error={errors?.endDate}
              id="input-adv-periodo-fim"
              format="DD/MM/YYYY"
              guide
            />
          </Col>
        </Row>
      }
      footer={
        <Row style={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Col xs="auto">
            <Text type="regular" color="dark" weight="400">
              {activeCount > 0
                ? `${activeCount} filtro${activeCount > 1 ? 's' : ''} ativo${activeCount > 1 ? 's' : ''}`
                : 'Nenhum filtro aplicado'}
            </Text>
          </Col>
          <Col xs="auto" className="d-flex" style={{ gap: 8 }}>
            <Button variant="secondary" onClick={onClose} id="button-adv-cancelar">
              <Text weight={500} type="regular">Cancelar</Text>
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              disabled={errors}
              id="button-adv-filtrar"
            >
              <Text weight={500} type="regular">Filtrar</Text>
            </Button>
          </Col>
        </Row>
      }
    />
  );
}